import { useCallback, useEffect, useRef, useState } from "react"

import { logger } from "src/utils/datadog"

const SIGNATURE_RETRY_CODE = "signature_not_confirmed"
const SIGNATURE_RETRIES = 2
const SIGNATURE_RETRY_MS = 2000

const useSignedAutoSubmit = ({
  quoteId,
  signedPhase,
  submitFailure,
  isLoading,
  submitted,
  declinedOrExpired,
  submit,
}) => {
  const submitStarted = useRef(false)
  const signatureRetries = useRef(0)
  const [retryPending, setRetryPending] = useState(false)

  const retrySilently = useCallback(
    (error) => {
      if (
        error.code === SIGNATURE_RETRY_CODE &&
        signatureRetries.current < SIGNATURE_RETRIES
      ) {
        signatureRetries.current += 1
        logger.info("Docuseal signature retry", {
          quoteId,
          attempt: signatureRetries.current,
          code: error.code,
        })
        setRetryPending(true)
        return true
      }

      signatureRetries.current = 0
      return false
    },
    [quoteId]
  )

  useEffect(() => {
    if (!retryPending) return

    const timer = setTimeout(() => {
      submitStarted.current = false
      setRetryPending(false)
    }, SIGNATURE_RETRY_MS)

    return () => clearTimeout(timer)
  }, [retryPending])

  useEffect(() => {
    if (!signedPhase) {
      submitStarted.current = false
      signatureRetries.current = 0
      return
    }

    if (submitStarted.current || retryPending) return

    // A failure already showing is this entry's outcome; Try Again resubmits.
    if (submitFailure) {
      submitStarted.current = true
      return
    }

    if (isLoading || submitted || declinedOrExpired) return

    submitStarted.current = true
    submit()
  }, [
    signedPhase,
    retryPending,
    submitFailure,
    isLoading,
    submitted,
    declinedOrExpired,
    submit,
  ])

  return { retrySilently }
}

export default useSignedAutoSubmit
